import { useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { glide, pose } from "./Player";
import { markVisited, nextPlace, placePoint, visitedSet } from "./Guide";
import { MEMORY_STAND, WINDOWS } from "../../lib/world";
import { day } from "../../lib/daylight";

const N = 48;
const vert = `attribute float aAlong; attribute float aAcross; varying float vAlong; varying float vAcross;
void main(){ vAlong = aAlong; vAcross = aAcross; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
// a flowing ribbon of light along the floor, brighter pulses travelling toward the next place
const frag = `uniform float uTime, uLen, uOn, uMotion; varying float vAlong; varying float vAcross;
void main(){
  float tm = uTime * uMotion;
  float flow = pow(0.5 + 0.5 * sin(vAlong * uLen * 2.6 - tm * 2.2), 3.0);
  float edge = 1.0 - smoothstep(0.25, 1.0, abs(vAcross));
  float ends = smoothstep(0.0, 0.12, vAlong) * (1.0 - smoothstep(0.82, 1.0, vAlong));
  float a = edge * ends * (0.28 + 0.72 * flow) * 0.5 * uOn;
  gl_FragColor = vec4(vec3(1.0, 0.8, 0.45) * a, 1.0);
}`;

export function FloorPath({ active }) {
  const { geo, mat } = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array((N + 1) * 2 * 3), 3));
    const al = new Float32Array((N + 1) * 2);
    const ac = new Float32Array((N + 1) * 2);
    const idx = [];
    for (let i = 0; i <= N; i++) {
      al[i * 2] = al[i * 2 + 1] = i / N;
      ac[i * 2] = -1;
      ac[i * 2 + 1] = 1;
      if (i < N) idx.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2);
    }
    g.setAttribute("aAlong", new THREE.BufferAttribute(al, 1));
    g.setAttribute("aAcross", new THREE.BufferAttribute(ac, 1));
    g.setIndex(idx);
    const m = new THREE.ShaderMaterial({ vertexShader: vert, fragmentShader: frag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, uniforms: { uTime: { value: 0 }, uLen: { value: 6 }, uOn: { value: 0 }, uMotion: day.uMotion } });
    return { geo: g, mat: m };
  }, []);
  const st = useMemo(() => ({ t: 0, on: 0, w: 0, m: 0 }), []);
  useFrame((_, dt) => {
    st.t += dt;
    mat.uniforms.uTime.value += dt;
    if (active) {
      // a place counts as visited after a short dwell, not a walk-past
      st.w = Math.hypot(pose.x - WINDOWS.cx, pose.z - (WINDOWS.z - 3)) < 4 ? st.w + dt : 0;
      const face = Math.abs(Math.atan2(Math.sin(pose.yaw - MEMORY_STAND.yaw), Math.cos(pose.yaw - MEMORY_STAND.yaw))) < 0.6;
      st.m = (window.__afaeBenchOpen || (face && Math.hypot(pose.x - MEMORY_STAND.x, pose.z - MEMORY_STAND.z) < 1)) ? st.m + dt : 0;
      if (st.w > 3) markVisited("windows");
      if (st.m > 3) markVisited("memory");
    }
    const next = active ? nextPlace(visitedSet()) : null;
    const p = next && placePoint(next);
    window.__afaeNext = p ? { id: next, x: p.x, z: p.z } : null;
    const d = p ? Math.hypot(p.x - pose.x, p.z - pose.z) : 0;
    const want = p && d > 1.6 && !glide.on ? 1 : 0;
    st.on += (want - st.on) * Math.min(1, dt * 2);
    mat.uniforms.uOn.value = st.on;
    if (!p || st.t < 0.3) return;
    st.t = 0;
    const fx = -Math.sin(pose.yaw), fz = -Math.cos(pose.yaw);
    const sx = pose.x + fx * 0.9, sz = pose.z + fz * 0.9;
    const cx = (sx + p.x) / 2 + (p.z - sz) * 0.12, cz = (sz + p.z) / 2 - (p.x - sx) * 0.12;
    const pos = geo.attributes.position;
    let px = sx, pz = sz;
    for (let i = 0; i <= N; i++) {
      const t = i / N, u = 1 - t;
      const x = u * u * sx + 2 * u * t * cx + t * t * p.x;
      const z = u * u * sz + 2 * u * t * cz + t * t * p.z;
      let tx = x - px, tz = z - pz;
      if (i === 0) [tx, tz] = [cx - sx, cz - sz];
      const l = Math.hypot(tx, tz) || 1;
      const w = 0.2;
      pos.setXYZ(i * 2, x - (tz / l) * w, 0.025, z + (tx / l) * w);
      pos.setXYZ(i * 2 + 1, x + (tz / l) * w, 0.025, z - (tx / l) * w);
      px = x;
      pz = z;
    }
    pos.needsUpdate = true;
    mat.uniforms.uLen.value = Math.max(2, d);
  });
  return <mesh data-testid="floor-path" geometry={geo} material={mat} frustumCulled={false} renderOrder={4} raycast={() => null} />;
}
